<template>
    <div class="lead-form-submission">
        <div class="lead-form-submission__body">
            <h6
                class="lead-form-submission__body-title tw-mb-6 lg:tw-mb-4 tw-text-base lg:tw-text-lg tw-leading-base tw-font-semibold"
                :class="{ 'tw-hidden': hasDealerSelectionLayout }"
            >
                {{ $t('configuration-wizard.lead-form.title') }}
            </h6>
            <lead-form-wrapper
                ref="leadFormWrapper"
                :form-id="formId"
                :form-data="dataForForm"
                @mounted="onLeadFormMounted"
            />
        </div>
    </div>
</template>

<script>
/**
 * A lead form component
 */
export default {
    name: 'LeadFormSubmission',
    components: {
        LeadFormWrapper: () => ({
            component: import(
                /* webpackChunkName: "LeadFormWrapper-vue" */ '@v4/common/components/organisms/LeadFormWrapper/LeadFormWrapper'
            ),
        }),
    },
    props: {
        /**
         * Selected color object
         */
        activeColor: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Selected version object
         */
        selectedVersion: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Selected optionals array
         */
        selectedOptionals: {
            type: Array,
            default: () => [],
        },
        /**
         * Form Id.
         * Available values: `submodel`, `version`, `social-lp`, `widget`, `advisor`
         * `editorial`, `multimodel`, `single-offer`, `offer-landing`, `compare`, `dkpromo`
         */
        formId: {
            type: String,
            required: true,
        },
        /**
         * Data to send when fetching the form configuration
         */
        formData: {
            type: Object,
            default: null,
        },
        /**
         * Make name
         */
        make: {
            type: String,
            default: '',
        },
        /**
         * Model name
         */
        model: {
            type: String,
            default: '',
        },
        /**
         * Whether or not to apply custom styling
         * when the form is not inside a modal
         * (e.g. on the Configuration Wizard)
         */
        isInPage: {
            type: Boolean,
            default: false,
        },
        hasDealerSelectionLayout: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            leadFormReady: false,
        };
    },
    computed: {
        versionName() {
            const version = this.selectedVersion || {};
            return version.versionName || '';
        },
        basePrice() {
            const version = this.selectedVersion || {};
            return (version.pricing && version.pricing.basePrice) || 0;
        },
        dataForForm() {
            return {
                ...this.formData,
                ...(this.isInPage && { isInPage: this.isInPage }),
            };
        },
    },
    watch: {
        activeColor(color) {
            this.setLeadFormColorConfiguration(color);
        },
        selectedVersion(version) {
            this.setLeadFormVersionConfiguration(version);
        },
        selectedOptionals(optionals) {
            this.setLeadFormOptionalsConfiguration(optionals);
        },
    },
    methods: {
        onLeadFormMounted() {
            this.leadFormReady = true;

            if (this.activeColor) {
                this.setLeadFormColorConfiguration(this.activeColor);
            }
            this.setLeadFormVersionConfiguration(this.selectedVersion);
            this.setLeadFormOptionalsConfiguration(this.selectedOptionals);
        },
        setLeadFormColorConfiguration(color = {}) {
            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorDescription]', color.description || '');
            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorCode]', color.baseColourHex || '');
            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorType]', color.group || '');
            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorCalculated]', color.primaryHex || '');
            this.$refs.leadFormWrapper.setLeadFormHiddenField(
                'dkwp[secondaryColorCalculated]',
                color.secondaryHex || ''
            );
        },
        setLeadFormVersionConfiguration(version = {}) {
            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[vehicleId]', version.versionHistoricalId);
        },
        setLeadFormOptionalsConfiguration(optionals = []) {
            const optionalsString = optionals.length ? optionals.map((item) => item.description).join() : null;
            const finalPrice = optionals.reduce((a, b) => a + b.price, this.basePrice);

            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[price]', finalPrice.toString());
            this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[optional]', optionalsString);
        },
    },
};
</script>
